import { esc, refreshNotifyEmail } from '../dom.js';
import { ST } from '../state.js';
import { buildUploadImgMap } from './helpers.js';

// ── SITE EXPORT & FORM HELPERS ────────────────────────────────────
// Prepare site files for zip/publish, form meta injection, token value helper.

// Pure HTML transformation — no DOM dependency. Direct migration to Vue utils.

// Strip builder-only CSS rules, click-to-select JS, and selection classes from exported HTML
function _stripBuilderArtifacts(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Remove sf-selected class from any section
  doc.querySelectorAll('.sf-selected').forEach(el => el.classList.remove('sf-selected'));

  // Remove inline edit highlight div
  const hlEl = doc.querySelector('.sf-hl');
  if (hlEl) {
    hlEl.remove();
  }

  // Remove contenteditable attributes
  doc.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));

  // Strip builder-only CSS from shell style — line-by-line removal is safest for minified CSS
  const shellStyle = doc.querySelector('style[data-shell]');
  if (shellStyle) {
    const builderPatterns = [
      'section{transition:opacity',
      '.sf-selected{',
      '.sf-selected::after{',
      '.sf-editing{',
      '@keyframes sf-pulse{',
      '.sf-hl{',
      '.sf-hl-visible',
      '.sf-hl-editing',
      '.sf-hl-dark',
      '.sf-hl-saved',
      '.sf-hl::before',
      '[contenteditable',
      '@media(pointer:coarse)',
    ];
    // Split on newlines (the shell CSS uses one rule per line in buildDesignShell)
    const lines = shellStyle.textContent.split('\n');
    shellStyle.textContent = lines
      .filter(line => !builderPatterns.some(p => line.includes(p)))
      .join('\n');
  }

  // Remove the inline edit + click-to-select blocks from the shell script
  // Inline edit starts at "// Inline text editing", click-to-select follows after
  // Truncate from the inline edit marker to end of script (removes both blocks)
  doc.querySelectorAll('script').forEach(s => {
    if (s.textContent.includes('sf-select') || s.textContent.includes('sf-inline-edit')) {
      const inlineMarker = s.textContent.indexOf('// Inline text editing');
      const selectMarker = s.textContent.indexOf('// Click-to-select section');
      const marker = inlineMarker >= 0 ? inlineMarker : selectMarker;
      if (marker >= 0) {
        s.textContent = s.textContent.substring(0, marker);
      }
    }
  });

  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

// ── PREPARE SITE FILES ──────────────────────────────────────────
// Central function that both downloadZip and publishToCloudflare use.
// Returns an array of { path, content } objects ready for packaging.
// Options:
//   baseUrl — absolute origin for sitemap URLs (e.g. 'https://my-site.pages.dev')
/**
 * Prepare site files for zip/publish packaging.
 * @param {{ baseUrl?: string }} [options]
 * @returns {Array<{path: string, content: string | Uint8Array, binary?: boolean}>}
 */
function prepareSiteFiles(options = {}) {
  const imgMap = buildUploadImgMap();
  const files = [];

  // Extract uploaded logo as a standalone file instead of inline data URL
  let logoHtmlPath = null;
  if (ST.logoImg?.data) {
    const mimeToExt = {
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/svg+xml': 'svg',
      'image/webp': 'webp',
      'image/gif': 'gif',
    };
    const ext = mimeToExt[ST.logoImg.type] || 'png';
    const logoFilePath = `assets/logo.${ext}`;
    logoHtmlPath = logoFilePath; // relative — adjusted per page depth below
    const bin = atob(ST.logoImg.data);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      arr[i] = bin.charCodeAt(i);
    }
    files.push({ path: logoFilePath, content: arr, binary: true });
  }

  ST.pages.forEach((p, i) => {
    if (!p.assembled) {
      return;
    }

    let html = refreshNotifyEmail(p.assembled)
      .replace(/\/api\/img-proxy\?src=([^"'\s)]+)/g, (m, encoded) => {
        try {
          return decodeURIComponent(encoded);
        } catch (_e) {
          return m;
        }
      })
      .replace(/__(?:UPLOADED|REPLACE_IMG)_[\w-]+__/g, match => {
        if (match === '__UPLOADED_LOGO__' && logoHtmlPath) {
          return i === 0 ? logoHtmlPath : `../${logoHtmlPath}`;
        }
        return imgMap[match] || match;
      });

    // Strip builder-only artifacts (click-to-select CSS/JS, selection classes)
    html = _stripBuilderArtifacts(html);

    const slug = p.name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    const path = i === 0 ? 'index.html' : `${slug}/index.html`;
    files.push({ path, content: html });
  });

  // Generate sitemap.xml
  if (files.length) {
    const base = (options.baseUrl || 'https://yoursite.com').replace(/\/+$/, '');
    const today = new Date().toISOString().slice(0, 10);
    const urls = files.map(f => {
      const loc = f.path === 'index.html' ? '/' : `/${f.path.replace(/\/index\.html$/, '/')}`;
      return `  <url>\n    <loc>${base}${loc}</loc>\n    <lastmod>${today}</lastmod>\n  </url>`;
    });
    const sitemap = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
      ...urls,
      '</urlset>',
    ].join('\n');
    files.push({ path: 'sitemap.xml', content: sitemap });
  }

  return files;
}

// ── FORM HELPERS ─────────────────────────────────────────────────

/**
 * Inject/fix form meta fields for contact and subscribe sections.
 * @param {string} html
 * @param {'contact' | 'subscribe'} formType
 * @returns {string}
 */
function injectFormMeta(html, formType) {
  const email = esc(ST.notificationEmail || '');
  const ft = formType === 'subscribe' ? 'subscribe' : 'contact';
  const siteName = esc(ST.concept?.siteName || '');

  // Replace placeholder from AI prompt
  html = html.replace(/NOTIFY_EMAIL_PLACEHOLDER/g, email);

  // Ensure form has action and method
  html = html.replace(/<form(?![^>]*action=)/gi, '<form action="/api/form-submit" method="POST"');

  // Build hidden fields to inject if missing
  let hiddens = '';
  if (!html.includes('name="__notify_email"')) {
    hiddens += `<input type="hidden" name="__notify_email" value="${email}">`;
  }
  if (!html.includes('name="__form_type"')) {
    hiddens += `<input type="hidden" name="__form_type" value="${ft}">`;
  }
  if (!html.includes('name="__site_name"')) {
    hiddens += `<input type="hidden" name="__site_name" value="${siteName}">`;
  }

  // Inject after first <form...>
  if (hiddens) {
    html = html.replace(/<form([^>]*)>/i, `<form$1>${hiddens}`);
  }

  return html;
}

// ── TOKEN HELPERS ─────────────────────────────────────────────────

// Extract raw value from a token (object with .value) or plain string
function val(token, fallback = '') {
  if (!token) {
    return fallback;
  }
  if (typeof token === 'object') {
    return token.value || fallback;
  }
  return token;
}

// ── MODULE EXPORTS ──────
export { _stripBuilderArtifacts, injectFormMeta, prepareSiteFiles, val };
